import { useState, useEffect } from "react";
import { useThemeContext } from "../theme/ThemeContext";
import { StarMark, Button } from "./shared";
import { ChangelogModal } from "./modals/ChangelogModal";
import { exportCSV } from "../utils/exportUtils";
import { CHANGELOG } from "../utils/changelog";
import { formatDateLabel } from "../utils/timeUtils";
import { isNative } from "../utils/nativePush";
import { useLayout } from "../hooks/useWindowWidth";

export function todayISO() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
}

const FONT_SCALE_OPTIONS = [
  { label: "A", scale: 1,    name: "Normal" },
  { label: "A", scale: 1.25, name: "Large"  },
];

const MenuIcons = {
  calendar: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M1.5 6.5H14.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M5 1.5V3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M11 1.5V3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="5" cy="10" r="0.8" fill="currentColor"/>
      <circle cx="8" cy="10" r="0.8" fill="currentColor"/>
      <circle cx="11" cy="10" r="0.8" fill="currentColor"/>
    </svg>
  ),
  person: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M2.5 13.5C2.5 11.015 5.015 9 8 9C10.985 9 13.5 11.015 13.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  externalLink: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M9 3H13V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13 3L7 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M7 4H3.5C2.948 4 2.5 4.448 2.5 5V12.5C2.5 13.052 2.948 13.5 3.5 13.5H11C11.552 13.5 12 13.052 12 12.5V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  download: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2.5V10.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M4.5 7.5L8 11L11.5 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2.5 13.5H13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  lock: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M5 7V5C5 3.343 6.343 2 8 2C9.657 2 11 3.343 11 5V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="8" cy="10.5" r="1" fill="currentColor"/>
    </svg>
  ),
  key: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="5.5" cy="7" r="3" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M7.8 8.8L13.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M10.5 11.5L11.8 12.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  people: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.2" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M1.5 13C1.5 10.791 3.515 9 6 9C8.485 9 10.5 10.791 10.5 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="11" cy="5" r="1.8" stroke="currentColor" strokeWidth="1.2" opacity="0.6"/>
      <path d="M12.5 9.3C13.7 9.8 14.5 10.8 14.5 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
    </svg>
  ),
  upload: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 10.5V2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M4.5 5.5L8 2L11.5 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2.5 13.5H13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  signOut: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 2.5H3.5C2.948 2.5 2.5 2.948 2.5 3.5V12.5C2.5 13.052 2.948 13.5 3.5 13.5H6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M10.5 5L13.5 8L10.5 11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13.5 8H6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  feedback: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 2.5H13.5C13.776 2.5 14 2.724 14 3V10C14 10.276 13.776 10.5 13.5 10.5H5.5L2 14V3C2 2.724 2.224 2.5 2.5 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.5 6H10.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M5.5 8.5H8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  inbox: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 9.5H5.5L6.5 11.5H9.5L10.5 9.5H14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2.5 4.5H13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M2.5 7H13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M2 9.5V13C2 13.276 2.224 13.5 2.5 13.5H13.5C13.776 13.5 14 13.276 14 13V9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  phone: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 3.5C2.5 2.948 2.948 2.5 3.5 2.5H5.5C5.776 2.5 6 2.724 6 3L6.75 5.75C6.75 6.026 6.526 6.25 6.25 6.25L4.75 6.75C5.493 8.561 6.939 9.843 8.75 10.75L9.25 9.25C9.474 8.974 9.698 8.75 9.974 8.75L12.75 9.5C13.026 9.5 13.25 9.724 13.25 10V12C13.25 12.552 12.802 13 12.25 13C6.865 13 2.5 8.635 2.5 3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  bell: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2C8 2 5 3.5 5 7.5V11.5H11V7.5C11 3.5 8 2 8 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3.5 11.5H12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M6.5 11.5C6.5 12.328 7.172 13 8 13C8.828 13 9.5 12.328 9.5 11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  bellOff: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2C8 2 5 3.5 5 7.5V11.5H11V7.5C11 3.5 8 2 8 2Z" stroke="currentColor" opacity="0.4" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3.5 11.5H12.5" stroke="currentColor" opacity="0.4" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M6.5 11.5C6.5 12.328 7.172 13 8 13C8.828 13 9.5 12.328 9.5 11.5" stroke="currentColor" opacity="0.4" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M2.5 2.5L13.5 13.5" stroke="currentColor" opacity="0.4" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  sun: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M8 1.5V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M8 13V14.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M1.5 8H3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M13 8H14.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M3.4 3.4L4.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M11.5 11.5L12.6 12.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M12.6 3.4L11.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M4.5 11.5L3.4 12.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  moon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5A5.5 5.5 0 1 0 13.5 9.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  shield: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L2.5 3.5V8C2.5 11 5 13.5 8 14.5C11 13.5 13.5 11 13.5 8V3.5L8 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  chart: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="8" width="2.5" height="5.5" rx="0.5" fill="currentColor" opacity="0.7"/>
      <rect x="6" y="5" width="2.5" height="8.5" rx="0.5" fill="currentColor" opacity="0.85"/>
      <rect x="10" y="2.5" width="2.5" height="11" rx="0.5" fill="currentColor"/>
    </svg>
  ),
  summary: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
      <rect x="9" y="9" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  ),
  intel: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 11.5L5.5 7.5L8.5 9.5L13 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M11 4H13V6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  warRoom: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M8 8 L8 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="8" cy="8" r="1.2" fill="currentColor"/>
      <path d="M5 4.5 L2.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M11 4.5 L13.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  expeditor: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="5.5" cy="4" r="1.8" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M2 13.5C2 11.015 3.57 9 5.5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M5.5 9L8 6.5L11 8.5L14 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 5.5H14V7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  clock: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M8 5V8.5L10.5 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  pause: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3.5" y="3" width="3" height="10" rx="1" fill="currentColor"/>
      <rect x="9.5" y="3" width="3" height="10" rx="1" fill="currentColor"/>
    </svg>
  ),
  play: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4.5 3.5L13 8L4.5 12.5V3.5Z" fill="currentColor"/>
    </svg>
  ),
  expedite: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 4L7 8L3 12M8.5 4L12.5 8L8.5 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

export default function HamburgerMenu({
  appDate, entries, shift, isUA, activeSCCO, isAdmin,
  onChangeDate, onManagePasswords, onManageRoster, onImportExcel,
  onChangeMyPassword, onExportShift, onPhoneDirectory, onSignOut, onClose,
  pushEnabled, onTogglePush, onFeedback, onFeedbackInbox, unreadFeedback,
  onLoginAlerts, lockoutAlertCount, onLoginLog, onActivityHeatmap,
  onOpsIntel, onWarRoom, onExpeditorMode, isDark, onToggleTheme, fontScale, onSetFontScale,
  syncEnabled, onToggleSync, tdtWriteback, onCycleTdtWriteback,
}) {
  const { SA, GLASS, tokens, TYPOGRAPHY, RADIUS } = useThemeContext();
  const [showChangelog, setShowChangelog] = useState(false);
  const [hovered, setHovered] = useState(null);
  // Dry Run → On is the step that makes expedites real: it takes a second tap within 4s
  const [armWritebackOn, setArmWritebackOn] = useState(false);
  useEffect(() => {
    if (!armWritebackOn) return;
    const t = setTimeout(() => setArmWritebackOn(false), 4000);
    return () => clearTimeout(t);
  }, [armWritebackOn]);

  const isToday      = appDate === todayISO();
  const isIpad       = /iPad/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isPhone      = /iPhone|Android.*Mobile|Mobile.*Android/i.test(navigator.userAgent);
  const isPWA        = window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone;
  const isMacDesktop = !isPhone && !isIpad && /Mac/.test(navigator.platform || navigator.userAgent);
  // Phones are excluded, except a native device whose screen is large in both
  // directions, i.e. the iPhone Duo unfolded (its user agent still says iPhone).
  const { isLargeScreen } = useLayout();
  const showMissionControl = isToday && (!isPhone || isLargeScreen);

  const uaGroups = [
    [
      {
        label: "Change Date",
        sub: formatDateLabel(appDate),
        icon: MenuIcons.calendar,
        action: () => { onClose(); onChangeDate(); }
      },
    ],
    [
      {
        label: "Expeditor Mode",
        sub: "Gate sign & flight queue",
        icon: MenuIcons.expeditor,
        action: () => { onClose(); onExpeditorMode(); }
      },
      {
        label: "Phone Directory",
        sub: "Airline contacts & tap-to-dial",
        icon: MenuIcons.phone,
        action: () => { onClose(); onPhoneDirectory(); }
      },
      {
        label: "TDT Tool",
        sub: "tdt.staralliance.com",
        icon: MenuIcons.externalLink,
        action: () => { window.open("https://tdt.staralliance.com/starmoderntdt/connection/updateExpedite/", "_blank", "noopener,noreferrer"); onClose(); }
      },
      ...(showMissionControl ? [{
        label: "Mission Control",
        sub: "Live ops map with aircraft positions and MCT risk",
        icon: MenuIcons.warRoom,
        action: () => { onClose(); onWarRoom(); },
      }] : []),
      {
        label: "Export Shift as Report",
        sub: `${entries.length} connection${entries.length !== 1 ? "s" : ""}`,
        icon: MenuIcons.download,
        action: () => { onClose(); onExportShift(); },
        disabled: entries.length === 0
      },
      {
        label: "Import Excel",
        sub: "Bulk import connections",
        icon: MenuIcons.upload,
        action: () => { onClose(); onImportExcel(); }
      },
    ],
    [
      {
        label: isNative() ? "Push Notifications: ON" : (pushEnabled ? "Push Notifications: ON" : "Push Notifications: OFF"),
        sub: isNative() ? "Managed by iOS Settings" : (pushEnabled ? "Tap to disable" : "Notify me of new connections & messages"),
        icon: pushEnabled ? MenuIcons.bell : MenuIcons.bellOff,
        action: () => { if (!isNative()) onTogglePush(); }
      },
    ],
    [
      {
        label: isDark ? "Switch to Light Mode" : "Switch to Dark Mode",
        sub:   isDark ? "Lighter interface" : "Darker interface",
        icon:  isDark ? MenuIcons.sun : MenuIcons.moon,
        action: () => { onToggleTheme(); }
      }
    ],
    [
      {
        label: "Change My Password",
        sub: "Update your login password",
        icon: MenuIcons.lock,
        action: () => { onClose(); onChangeMyPassword(); }
      },
      ...(isAdmin ? [
        {
          label: "Manage Passwords",
          sub: "Partner airline access",
          icon: MenuIcons.key,
          action: () => { onClose(); onManagePasswords(); }
        },
        {
          label: "SCCO Roster",
          sub: "Agents & passwords",
          icon: MenuIcons.people,
          action: () => { onClose(); onManageRoster(); }
        },
        {
          label: "Feedback Inbox",
          sub: unreadFeedback > 0 ? `${unreadFeedback} open report${unreadFeedback !== 1 ? "s" : ""}` : "Bug reports & suggestions",
          icon: MenuIcons.inbox,
          action: () => { onClose(); onFeedbackInbox(); },
          badge: unreadFeedback > 0 ? unreadFeedback : null,
        },
        {
          label: "Security Alerts",
          sub: lockoutAlertCount > 0 ? `${lockoutAlertCount} lockout event${lockoutAlertCount !== 1 ? "s" : ""}` : "Login lockout events",
          icon: MenuIcons.shield,
          action: () => { onClose(); onLoginAlerts(); },
          badge: lockoutAlertCount > 0 ? lockoutAlertCount : null,
        },
        {
          label: "Login Log",
          sub: "Who signed in — last 7 days",
          icon: MenuIcons.clock,
          action: () => { onClose(); onLoginLog(); },
        },
        {
          label: "Activity Heatmap",
          sub: "Partner message activity by hour",
          icon: MenuIcons.chart,
          action: () => { onClose(); onActivityHeatmap(); },
        },
        {
          label: "Ops Intelligence",
          sub: "Today's KPIs, flights, airlines, routes, SCCO trends",
          icon: MenuIcons.intel,
          action: () => { onClose(); onOpsIntel(); },
        },
        {
          label: syncEnabled ? "Pause TDT Auto-Sync" : "Resume TDT Auto-Sync",
          sub: syncEnabled ? "Auto-import is running" : "Auto-import is paused",
          icon: syncEnabled ? MenuIcons.pause : MenuIcons.play,
          action: () => { onToggleSync(); },
        },
      ] : []),
    ],
    // Own group, away from the auto-sync toggle: this one decides whether expedites really reach TDT
    ...(isAdmin ? [[
      armWritebackOn ? {
        label: "Tap again to turn On",
        sub: "Expedites will really be logged in TDT",
        icon: MenuIcons.expedite,
        tone: tokens.warning,
        action: () => { setArmWritebackOn(false); onCycleTdtWriteback(); },
      } : {
        label: `TDT Expedites: ${{ off: "Off", dry_run: "Dry Run", on: "On" }[tdtWriteback] || "Off"}`,
        sub: { off: "Hidden from cards. Tap for Dry Run", dry_run: "Logged only, nothing sent. Tap to turn On", on: "Sent to TDT for real. Tap to turn Off" }[tdtWriteback] || "",
        icon: MenuIcons.expedite,
        tone: tdtWriteback === "on" ? tokens.warning : undefined,
        action: () => { if (tdtWriteback === "dry_run") setArmWritebackOn(true); else onCycleTdtWriteback(); },
      },
    ]] : []),
    [
      {
        label: "Report a Bug / Suggestion",
        sub: "Send feedback to the admin team",
        icon: MenuIcons.feedback,
        action: () => { onClose(); onFeedback(); }
      },
    ],
  ];

  const partnerGroups = [
    [
      {
        label: "Change Date",
        sub: formatDateLabel(appDate),
        icon: MenuIcons.calendar,
        action: () => { onClose(); onChangeDate(); }
      },
      {
        label: "Export as Report",
        sub: `${entries.length} connection${entries.length !== 1 ? "s" : ""}`,
        icon: MenuIcons.download,
        action: () => { exportCSV(entries, formatDateLabel(appDate)); onClose(); },
        disabled: entries.length === 0
      },
      ...(showMissionControl ? [{
        label: "Mission Control",
        sub: "Live ops map with aircraft positions and MCT risk",
        icon: MenuIcons.warRoom,
        action: () => { onClose(); onWarRoom(); },
      }] : []),
    ],
    [
      {
        label: "Expeditor Mode",
        sub: "Gate sign & flight queue",
        icon: MenuIcons.expeditor,
        action: () => { onClose(); onExpeditorMode(); }
      },
      {
        label: "Phone Directory",
        sub: "Airline contacts & tap-to-dial",
        icon: MenuIcons.phone,
        action: () => { onClose(); onPhoneDirectory(); }
      },
    ],
    [
      {
        label: isNative() ? "Push Notifications: ON" : (pushEnabled ? "Push Notifications: ON" : "Push Notifications: OFF"),
        sub: isNative() ? "Managed by iOS Settings" : (pushEnabled ? "Tap to disable" : "Notify me of new connections & messages"),
        icon: pushEnabled ? MenuIcons.bell : MenuIcons.bellOff,
        action: () => { if (!isNative()) onTogglePush(); }
      },
    ],
    [
      {
        label: isDark ? "Switch to Light Mode" : "Switch to Dark Mode",
        sub:   isDark ? "Lighter interface" : "Darker interface",
        icon:  isDark ? MenuIcons.sun : MenuIcons.moon,
        action: () => { onToggleTheme(); }
      }
    ],
    [
      {
        label: "Report a Bug / Suggestion",
        sub: "Send feedback to the admin team",
        icon: MenuIcons.feedback,
        action: () => { onClose(); onFeedback(); }
      },
    ],
  ];

  const groups = isUA ? uaGroups : partnerGroups;

  const itemStyle = (disabled, isHovered) => ({
    width: "100%", background: isHovered && !disabled ? (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)") : "transparent",
    border: "none", padding: "12px 20px", cursor: disabled ? "not-allowed" : "pointer",
    display: "flex", alignItems: "center", gap: 14, textAlign: "left",
    opacity: disabled ? 0.32 : 1, transition: "background 0.12s", borderRadius: RADIUS.sm,
  });

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          zIndex: 300, backdropFilter: "blur(2px)" }} />

      {/* Drawer */}
      <div style={{
        ...GLASS.panel,
        background: isDark ? "rgba(10,13,26,0.88)" : "rgba(255,255,255,0.88)",
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "calc(300px + env(safe-area-inset-right, 0px))", paddingRight: "env(safe-area-inset-right, 0px)", boxSizing: "border-box",
        zIndex: 301, display: "flex", flexDirection: "column",
        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
        animation: "drawerSlideIn 0.22s cubic-bezier(0.16,1,0.3,1)",
        overflowY: "auto",
        boxShadow: "-12px 0 48px rgba(0,0,0,0.45)",
      }}>

        {/* Header */}
        <div style={{ padding: "calc(env(safe-area-inset-top, 0px) + 24px) 20px 18px", borderBottom: `1px solid ${SA.borderGray}` }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <StarMark size={13} color={SA.silver} />
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.22em",
                  textTransform: "uppercase", fontWeight: 500 }}>
                  {isUA ? "United Airlines" : (activeSCCO?.airline || "Partner")}
                </span>
              </div>
              {isUA && activeSCCO ? (
                <>
                  <div style={{ fontSize: TYPOGRAPHY.header, fontWeight: 600, color: SA.white,
                    letterSpacing: "-0.02em", lineHeight: 1.2 }}>{activeSCCO.name}</div>
                  {activeSCCO.badge && (
                    <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 3,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>#{activeSCCO.badge}</div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 15, fontWeight: 600, color: SA.white,
                  letterSpacing: "-0.01em" }}>Menu</div>
              )}
            </div>
            <Button variant="icon" onClick={onClose}
              style={{ background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)", border: `1px solid ${SA.borderGray}`,
                color: SA.silver, width: 30, height: 30, fontSize: 16,
                transition: "background 0.15s", flexShrink: 0, marginTop: 2 }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}
              onMouseLeave={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)"}>
              ×
            </Button>
          </div>
        </div>

        {/* Menu groups */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
          {groups.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && (
                <div style={{ height: 1, background: SA.borderGray, margin: "6px 8px 6px" }} />
              )}
              {group.map((item, ii) => {
                const key = `${gi}-${ii}`;
                return (
                  <Button key={key}
                    onClick={item.disabled ? undefined : item.action}
                    onMouseEnter={() => setHovered(key)}
                    onMouseLeave={() => setHovered(null)}
                    style={itemStyle(item.disabled, hovered === key)}>
                    <div style={{ width: 32, height: 32, borderRadius: RADIUS.sm, flexShrink: 0,
                      background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)", border: `1px solid ${SA.borderGray}`,
                      color: SA.white,
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {item.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: TYPOGRAPHY.note, color: item.tone || SA.white, fontWeight: 500,
                        letterSpacing: "-0.01em", marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                        {item.label}
                        {item.badge > 0 && <span style={{ background: tokens.dangerMuted, color: "#fff", borderRadius: RADIUS.md, padding: "1px 6px", fontSize: TYPOGRAPHY.tiny, fontWeight: 700 }}>{item.badge}</span>}
                      </div>
                      <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.sub}
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          ))}

          {/* Text Size */}
          <div style={{ height: 1, background: SA.borderGray, margin: "6px 8px" }} />
          <div style={{ padding: "10px 8px 6px" }}>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.12em",
              textTransform: "uppercase", fontWeight: 500, marginBottom: 8, paddingLeft: 4 }}>
              Text Size
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {FONT_SCALE_OPTIONS.map(opt => {
                const active = fontScale === opt.scale;
                return (
                  <button key={opt.scale} onClick={() => onSetFontScale(opt.scale)}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "8px 10px", borderRadius: RADIUS.sm, cursor: "pointer",
                      border: `1px solid ${active ? "rgba(91,142,255,0.6)" : SA.borderGray}`,
                      background: active ? (isDark ? "rgba(91,142,255,0.18)" : "rgba(37,99,235,0.10)") : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"),
                      color: active ? (isDark ? "#7ba7ff" : "#2563eb") : SA.silver,
                      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                      transition: "all 0.15s",
                    }}>
                    <span style={{ fontSize: opt.scale === 1 ? 12 : 16, fontWeight: 700, lineHeight: 1 }}>{opt.label}</span>
                    <span style={{ fontSize: 11 }}>{opt.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sign out */}
          <div style={{ height: 1, background: SA.borderGray, margin: "6px 8px" }} />
          {(() => {
            const key = "signout";
            return (
              <Button
                onClick={() => { onClose(); onSignOut(); }}
                onMouseEnter={() => setHovered(key)}
                onMouseLeave={() => setHovered(null)}
                style={itemStyle(false, hovered === key)}>
                <div style={{ width: 32, height: 32, borderRadius: RADIUS.sm, flexShrink: 0,
                  background: tokens.dangerBgGlow, border: `1px solid ${tokens.dangerBorderLo}`,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {MenuIcons.signOut}
                </div>
                <div>
                  <div style={{ fontSize: TYPOGRAPHY.note, color: tokens.danger, fontWeight: 500,
                    letterSpacing: "-0.01em", marginBottom: 2 }}>Sign Out</div>
                  <div style={{ fontSize: TYPOGRAPHY.caption, color: tokens.dangerBorder }}>Return to login</div>
                </div>
              </Button>
            );
          })()}
        </div>

        {/* Version footer */}
        <div style={{ padding: "16px 20px", borderTop: `1px solid ${SA.borderGray}`,
          textAlign: "center" }}>
          <Button variant="ghost" onClick={() => setShowChangelog(true)}
            style={{ padding:"4px 10px", borderRadius:6, gap:6 }}>
            <span style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              letterSpacing: "0.08em" }}>Version {CHANGELOG[0].version}</span>
            <span style={{ fontSize:8, color:"#aaa", letterSpacing:"0.1em", textTransform:"uppercase" }}>
              What's new ›
            </span>
          </Button>
        </div>

        {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} />}

      </div>
    </>
  );
}
